import React, { FunctionComponent, useState, useEffect, useRef } from 'react';
import withStyles from 'react-jss';
import classnames from 'classnames';
import { Input, Button, Modal } from 'semantic-ui-react';

import { ClassProp } from 'helpers/types';
import { getStateAbbr } from 'helpers/USMapStates';
import { Theme } from 'config';

import { Place } from 'scenes/Home/scenes/MerchantVenuesOnboarding/MerchantVenuesOnboarding.helpers';
import { loadPlaceSearch } from './AddMissingVenueModal.helpers';

type LatLong = {
  lat: number;
  lng: number;
};

type Geometry = {
  type: string;
  coordinates: number[];
};

type Properties = {
  city: string;
  stateCode: string;
  postalCode: string;
  countryCode: string;
  street: string;
  type: string;
};

type MQPlace = {
  type: string;
  geometry?: Geometry;
  properties: Properties;
};

type MapQuestionResult = {
  type: string;
  name: string;
  city: string;
  state: string;
  stateCode: string;
  country: string;
  countryCode: string;
  postalCode: string;
  latlng: LatLong;
  searchAheadResult: {
    id: string;
    displayString: string;
    name: string;
    recordType: string;
    collection: string[];
    mqId: string;
    place: MQPlace;
  };
};

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  classes: Styles;
  onAddPlace: Function;
  noVenuesFound?: boolean;
};

const styles = (theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  triggerBtn: {
    color: `${theme.softViolet} !important`,
    outline: 'none !important',
    fontSize: '.875rem !important',
    background: 'transparent !important',
    border: 'none !important',
    padding: '.6625rem 1rem .6625rem !important',
    whiteSpace: 'nowrap !important',
    '&:hover': {
      opacity: 0.9,
    },
  },
  button: {
    backgroundColor: `${theme.violet} !important`,
    borderRadius: '1.5rem !important',
    color: '#fff !important',
    fontSize: '.875rem',
    margin: '.25rem .5rem !important',
    opacity: 1,
    padding: '.6625rem 1rem .6625rem !important',
    '&:hover': {
      opacity: 0.9,
    },
    '&:active': {
      opacity: 0.7,
    },
  },
  infoText: {
    display: 'block',
  },
  searchContainer: {
    display: 'flex',
  },
  missingVenueSection: {
    textAlign: 'left',
  },
  noVenuesButton: {
    background: '#683caf !important',
    color: 'white !important',
    padding: '.6625rem 1.5em .6625rem !important',
    borderRadius: '1.5rem !important',
    marginTop: '1rem !important',
    fontSize: '1rem !important',
  },
});

const setupPlaceSearch = (setSelectedVenue: Function) => () => {
  const ps = placeSearch({
    key: process.env.MAPQUEST_API_KEY,
    container: document.querySelector('#place-search-input'),
    collection: ['address', 'poi'],
  });

  ps.on('change', ({ result }: { result: MapQuestionResult }) => {
    setSelectedVenue(result);
  });

  ps.on('clear', () => {
    setSelectedVenue(null);
  });
};

const AddPlaceTypeAheadModal: FunctionComponent<Props> = ({
  classes,
  onAddPlace,
  noVenuesFound = false,
}) => {
  const searchInputRef = useRef<any>(null);
  const [selectedVenue, setSelectedVenue] = useState<MapQuestionResult | null>(
    null,
  );
  const [showModal, setShowModal] = useState<boolean>(false);

  useEffect(() => {
    if (showModal) {
      loadPlaceSearch(setupPlaceSearch(setSelectedVenue));
    }
  }, [showModal]);

  const toggleModal = () => setShowModal(!showModal);
  const addVenueString = !noVenuesFound
    ? `Don't see all your venues?`
    : `Add venues manually`;

  const handleAddPlace = () => {
    if (selectedVenue) {
      const { searchAheadResult, latlng } = selectedVenue;
      const { properties: addressProperties } = searchAheadResult.place;

      const newVenue: Place = {
        dataSource: 'MAPQUEST',
        externalId: searchAheadResult.id,
        name:
          searchAheadResult.recordType === 'poi' ? searchAheadResult.name : '',
        address: {
          street: addressProperties.street,
          suite: '',
          city: addressProperties.city,
          state: getStateAbbr(selectedVenue.state) as string,
          postal: addressProperties.postalCode,
          country: addressProperties.countryCode,
        },
        geocode: {
          longitude: latlng.lng,
          latitude: latlng.lat,
        },
      };

      onAddPlace(newVenue, searchAheadResult.id);
      setShowModal(!showModal);
    }
  };

  return (
    <div className={classes.missingVenueSection}>
      <Modal
        trigger={
          <Button
            className={classnames(classes.triggerBtn, {
              [classes.noVenuesButton]: noVenuesFound,
            })}
            onClick={toggleModal}
          >
            {addVenueString}
          </Button>
        }
        open={showModal}
        onClose={toggleModal}
      >
        <Modal.Header>Missing Something?</Modal.Header>
        <Modal.Content>
          <div className={classes.container}>
            <p className={classes.infoText}>
              Start typing below and we will try to find your location so you
              can easily add it to the list.
            </p>
            <div className={classes.searchContainer}>
              <Input
                type="search"
                id="place-search-input"
                placeholder="Start typing you missing address"
                ref={searchInputRef}
                style={{ flex: '1' }}
              />

              <Button
                className={classes.button}
                disabled={!selectedVenue}
                onClick={handleAddPlace}
              >
                Add Venue
              </Button>
            </div>
          </div>
        </Modal.Content>
      </Modal>
    </div>
  );
};

export default withStyles(styles)(AddPlaceTypeAheadModal);
